import { z } from "zod";
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

export const CitationSchema = z.object({
  // Library citations carry the source document UUID. AI/Web citations have
  // no backing document, so this is nullable+optional rather than a strict
  // uuid. (The emit path does not .parse(), so this is a type-contract relax.)
  documentId: z.string().nullable().optional(),
  docName: z.string(),
  page: z.number().int().nullable().default(null),
  section: z.string().nullable().default(null),
  text: z.string().max(1000),
  confidence: z.number().min(0).max(1).optional(),
  // ── Source-attribution tier (Library / AI / Web) ─────────────────────────
  // 'library' = customer-trusted knowledge (uploaded docs, wiki, external
  //             connectors, connected MCP servers) — verified by faithfulness.
  // 'ai'      = the model's own training knowledge — UNVERIFIED.
  // 'web'     = web_search result — UNVERIFIED external (carries `url`).
  // Defaults to 'library' so every existing + persisted citation stays in the
  // verified tier with zero migration.
  sourceType: z.enum(["library", "ai", "web"]).default("library"),
  // Sub-source label within a tier — e.g. the connector / MCP-server name, or
  // the web search engine. Null for plain uploaded docs (docName already
  // identifies them) and always null under ITAR.
  subSource: z.string().nullable().default(null),
  // Outbound link for web citations; null for library/ai. Kept as a loose
  // string (not z.string().url()) so unusual real-world URLs never reject.
  url: z.string().nullable().default(null),
  classification: z.object({
    sensitivity: SensitivitySchema.default("unclassified"),
    exportControl: ExportControlSchema.default("none"),
  }).optional(),
  // The 1-based position this source held in the list the MODEL was shown —
  // the N that the answer's "[Source N]" markers refer to. Emitted so the
  // renderer can resolve a marker EXPLICITLY instead of by array position:
  // the emitted list is filtered (uncited Library sources are dropped), so a
  // positional reading pointed at the wrong document whenever a cited source
  // had an uncited one before it, or fell out of bounds and silently stripped
  // the badge. Optional so citations persisted before this still validate;
  // the renderer falls back to the positional reading for those.
  sourceIndex: z.number().int().positive().optional(),
});

export type Citation = z.infer<typeof CitationSchema>;

export const CitationsArraySchema = z.array(CitationSchema);
export type Citations = z.infer<typeof CitationsArraySchema>;
